import React, { useEffect, useRef } from 'react';

const ParticleBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let mouse = { x: -1000, y: -1000 };
    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    };
    const handleMouseLeave = () => {
      mouse.x = -1000;
      mouse.y = -1000;
    };
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const particles: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      opacity: number;
      color: string;
    }> = [];

    const colors = [
      '#5e17eb', // neon-purple
      '#00d4ff', // cyber-blue
      '#ff2171', // pink-flare
      '#1ae2ff', // electric-blue
      '#ff2fd6', // magenta-neon
      '#ff4fd8', // neon-pink
      '#00fff7', // neon-cyan
      '#ffe600', // neon-yellow
      '#39ff14', // neon-green
      '#ff9900', // neon-orange
      '#ffffff',
    ];

    // Partículas más sutiles y sofisticadas
    for (let i = 0; i < 40; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        size: Math.random() * 1.2 + 0.6,
        opacity: Math.random() * 0.18 + 0.08,
        color: colors[Math.floor(Math.random() * colors.length)],
      });
    }

    let tick = 0;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      tick++;
      particles.forEach((particle, index) => {
        // Interacción con el mouse: repulsión suave
        const dx = particle.x - mouse.x;
        const dy = particle.y - mouse.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 100) {
          const angle = Math.atan2(dy, dx);
          const force = (100 - dist) / 100 * 1.2;
          particle.vx += Math.cos(angle) * force * 0.04;
          particle.vy += Math.sin(angle) * force * 0.04;
        }
        // Límite de velocidad
        particle.vx *= 0.98;
        particle.vy *= 0.98;
        // Movimiento normal
        particle.x += particle.vx;
        particle.y += particle.vy;

        // Parpadeo y cambio de color aleatorio
        if (Math.random() < 0.01) {
          particle.color = colors[Math.floor(Math.random() * colors.length)];
        }
        if (Math.random() < 0.02) {
          particle.opacity = 0.08 + Math.random() * 0.18;
        }
        // Efecto de pulso sutil
        if (index % 7 === 0) {
          particle.size = 0.8 + Math.abs(Math.sin(tick / 40 + index)) * 1.2;
        }

        // Wrap around screen
        if (particle.x < 0) particle.x = canvas.width;
        if (particle.x > canvas.width) particle.x = 0;
        if (particle.y < 0) particle.y = canvas.height;
        if (particle.y > canvas.height) particle.y = 0;

        // Draw particle
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = particle.color;
        ctx.globalAlpha = particle.opacity;
        ctx.shadowColor = particle.color;
        ctx.shadowBlur = 6;
        ctx.fill();
        ctx.shadowBlur = 0;

        // Conexiones sutiles
        particles.slice(index + 1).forEach(otherParticle => {
          const dx2 = particle.x - otherParticle.x;
          const dy2 = particle.y - otherParticle.y;
          const distance = Math.sqrt(dx2 * dx2 + dy2 * dy2);
          if (distance < 60 && Math.random() < 0.04) {
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(otherParticle.x, otherParticle.y);
            ctx.strokeStyle = particle.color;
            ctx.globalAlpha = (60 - distance) / 60 * 0.08;
            ctx.lineWidth = 0.3;
            ctx.stroke();
          }
        });
      });

      requestAnimationFrame(animate);
    };
    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-10"
      style={{ opacity: 0.4 }}
    />
  );
};

export default ParticleBackground;
